import { useRef, useEffect } from "react";

/**
 * The useClickAway hook is a useful for detecting clicks outside a specific component.
 * It allows you to pass a callback function that will be triggered whenever a click
 * occurs outside the component’s area. This hook is particularly helpful when implementing
 * dropdown menus, modals, or any other UI elements that need to be closed when the user
 * clicks outside of them. By attaching event listeners to the document, the hook checks if
 * the click target is within the component’s reference, and if not, it invokes the provided callback function.
 *
 * @see https://usehooks.com/useclickaway
 *
 * @param callback 	This function is invoked whenever a click event is detected outside of the referenced element. The event object from the click is passed to this callback function.
 * @returns This is a ref object returned by the hook. It should be attached to a React element to monitor click events. The ref provides a way to access the properties of the element it is attached to.
 */
export function useClickAway(callback: CallableFunction) {
    const ref = useRef<HTMLInputElement>(null);
    const refCallback = useRef(callback);

    useEffect(() => {
        const handler = (event: MouseEvent | TouchEvent) => {
            const element = ref.current;
            if (element && !element.contains(event.target as Node)) {
                refCallback.current(event);
            }
        };

        document.addEventListener("mousedown", handler);
        document.addEventListener("touchstart", handler);

        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("touchstart", handler);
        };
    }, []);

    return ref;
}
